<template>
    <div>
        <!--Edit Professional Dialog-->
        <Dialog :visible.sync="professionalDialog" :style="{width: '450px'}"
                header="Professional Qualifications Details"
                :modal="true" :maximizable="true" class="p-fluid">

            <div class="p-field" v-if="createProfessional">
                <label for="staff">Staff</label>
                <Dropdown id="staff" v-model="professional.staff"
                          :options="staffs" optionLabel="name" required="true" :filter="true"
                          :class="{'p-invalid': submitted && $v.professional.staff.$invalid}"/>
                <small class="p-invalid" v-if="submitted && $v.professional.staff.$invalid">Staff is
                    required.</small>
            </div>

            <div class="p-field" v-if="professionalMaster">
                <label for="title">Title</label>
                <Dropdown id="title" v-model="professionalMaster.title" :options="professionalTitles" optionLabel="name"
                          optionValue="name" required="true" :filter="true"
                          :class="{'p-invalid': submitted && $v.professionalMaster.title.$invalid}"/>
                <small class="p-invalid" v-if="submitted && $v.professionalMaster.title.$invalid">Title is
                    required.</small>
            </div>

            <div class="p-field" v-if="professionalMaster">
                <label for="full-title">Full Title</label>
                <InputText id="full-title" v-model="professionalMaster.description" disabled/>
            </div>

            <div class="p-field" v-if="professionalMaster">
                <label for="institution">Institution</label>
                <InputText id="institution" v-model.trim="professionalMaster.institution" disabled/>
            </div>

            <div class="p-field">
                <label for="description">Description</label>
                <InputText id="description" v-model.trim="professional.description" required="true"/>
            </div>

            <div class="p-field">
                <label for="date">Awarded Month (MM/YYYY)</label>
                <Calendar id="date" v-model="professional.date" view="month" dateFormat="mm/yy"
                          :yearNavigator="true" :yearRange="`1950:${new Date().getFullYear()}`" placeholder="MM/YYYY"
                          :class="{'p-invalid': submitted && $v.professional.date.$invalid}"/>
                <small class="p-invalid" style="margin-right: 1em"
                       v-if="submitted && !$v.professional.date.required">
                    Date is required.
                </small>
                <small class="p-invalid" v-if="submitted && !$v.professional.date.dateFormat">
                    Date is in wrong format.
                </small>
            </div>

            <template #footer>
                <Button label="Cancel" icon="pi pi-times" class="p-button-text" @click="hideDialog"/>
                <Button label="Save" icon="pi pi-check" class="p-button-text" @click="saveProfessional"/>
            </template>
        </Dialog>


        <!--Delete Professional Dialog-->
        <Dialog :visible.sync="deleteProfessionalDialog" :style="{width: '450px'}" header="Confirm" :modal="true">
            <div class="confirmation-content">
                <i class="pi pi-exclamation-triangle p-mr-3" style="font-size: 2rem"/>
                <span v-if="professional.professional">Are you sure you want to delete <b>{{professional.professional.title}}</b>?</span>
            </div>
            <template #footer>
                <Button label="No" icon="pi pi-times" class="p-button-text"
                        @click="deleteProfessionalDialog = false"/>
                <Button label="Yes" icon="pi pi-check" class="p-button-text" @click="deleteProfessional"/>
            </template>
        </Dialog>

        <!--Delete Professionals Dialog-->
        <Dialog :visible.sync="deleteProfessionalsDialog" :style="{width: '450px'}" header="Confirm" :modal="true">
            <div class="confirmation-content">
                <i class="pi pi-exclamation-triangle p-mr-3" style="font-size: 2rem"/>
                <span v-if="professional">Are you sure you want to delete the selected Professional Qualifications?</span>
            </div>
            <template #footer>
                <Button label="No" icon="pi pi-times" class="p-button-text"
                        @click="deleteProfessionalsDialog = false"/>
                <Button label="Yes" icon="pi pi-check" class="p-button-text"
                        @click="deleteSelectedProfessionals"/>
            </template>
        </Dialog>

        <!--Verify Professionals Dialog-->
        <Dialog :visible.sync="toggleProfessionalsDialog" :style="{width: '450px'}" header="Confirm" :modal="true">
            <div class="confirmation-content">
                <i class="pi pi-exclamation-triangle p-mr-3" style="font-size: 2rem"/>
                <span v-if="professional">Are you sure you want to {{ verifyProfessionals ? 'verify' : 'unverify' }} the selected professional?</span>
            </div>
            <template #footer>
                <Button label="No" icon="pi pi-times" class="p-button-text"
                        @click="toggleProfessionalsDialog = false"/>
                <Button label="Yes" icon="pi pi-check" class="p-button-text"
                        @click="toggleSelectedProfessionals"/>
            </template>
        </Dialog>
    </div>
</template>

<script>
    import {Component, Prop, Vue, Watch} from "vue-property-decorator";

    Component.registerHooks(['validations']);
    import {required} from 'vuelidate/lib/validators'
    import Toast from 'primevue/toast';
    import Dialog from "primevue/dialog";
    import Dropdown from 'primevue/dropdown';
    import InputText from 'primevue/inputtext';
    import Button from 'primevue/button';
    import InputSwitch from 'primevue/inputswitch';
    import Calendar from 'primevue/calendar';
    import ProfessionalService from "@/service/ProfessionalService";
    import StaffService from "@/service/StaffService";
    import store from "../../store";

    /**
     * Class-component
     */
    @Component({
        components: {
            Toast: Vue.extend(Toast),
            Dialog: Vue.extend(Dialog),
            Dropdown: Vue.extend(Dropdown),
            InputText: Vue.extend(InputText),
            Button: Vue.extend(Button),
            InputSwitch: Vue.extend(InputSwitch),
            Calendar: Vue.extend(Calendar)
        }
    })
    export default class ProfessionalDialog extends Vue {
        @Prop(Array) professionalTitles;

        createProfessional = false;
        professionalMaster = {};
        staffService = new StaffService();
        staffs = [];
        professionalService = new ProfessionalService();
        titles = [];
        professionalDialog = false;
        deleteProfessionalDialog = false;
        deleteProfessionalsDialog = false;
        toggleProfessionalsDialog = false;
        verifyProfessionals = false;
        professional = {};
        submitted = false;
        selectedProfessionals = [];
        toastSuccess = {
            severity: 'success',
            summary: 'Successful',
            detail: '',
            life: 3000
        };
        toastError = {
            severity: 'error',
            summary: 'Error',
            detail: '',
            life: 3000
        };

        validations() {
            const dateFormat = value => {
                if (typeof value === 'string' && !value.match(/^\d{1,2}\/\d{4}$/g)) return false
                return this.$moment(value, "MM/YYYY").isSameOrBefore(this.$moment())
            }
            return {
                professional: {
                    staff: {required},
                    date: {required, dateFormat}
                },
                professionalMaster: {
                    title: {required},
                }
            }
        }

        @Watch('createProfessional')
        async onCreateProfessionalChanged() {
            if (this.createProfessional) this.staffs = await this.staffService.getStaffs();
        }

        @Watch('professionalMaster.title')
        async onTitleChanged() {
            const data = await this.professionalService.getProfessionalFromTitle(this.professionalMaster.title)
            for (const dataKey in data) {
                if (Object.hasOwnProperty.call(data, dataKey)) this.professionalMaster = data[dataKey]
            }
        }

        @Watch('professional')
        async onProfessionalChanged() {
            if (this.professional.date) this.professional.date = this.$formatDate(this.professional.date)
        }

        hideDialog() {
            this.professionalDialog = false;
            this.submitted = false;
        }

        async saveProfessional() {
            this.submitted = true;

            if (this.$v.$invalid) {
                return; // invalid input
            }

            const professionalObj = {
                'verified': 0,
                _jv: {
                    type: 'staff-professionals',
                    relationships: {
                        professional: {data: {id: this.professionalMaster._jv.id, type: 'professionals'}},
                        staff: {data: {id: this.professional.staff._jv.id, type: 'staffs'}}
                    }
                }
            };

            professionalObj['description'] = this.professional['description']
            professionalObj['date'] = this.$moment(this.professional.date, "MM/YYYY").toISOString()

            try {
                if (this.professional._jv) {
                    // PATCH
                    professionalObj._jv.id = this.professional._jv.id
                    await this.professionalService.updateProfessional(professionalObj)
                } else {
                    // POST
                    await this.professionalService.createProfessional(professionalObj);
                }
                this.$emit('updated')
                this.toastSuccess.detail = 'Professional Qualifications Updated'
                this.$toast.add(this.toastSuccess);
            } catch (e) {
                this.toastError.detail = e.message
                this.$toast.add(this.toastError);
                this.professionalService.logError(e).forEach(err => {
                    this.$toast.add({
                        severity: 'error',
                        summary: 'Details',
                        detail: err
                    });
                })
            }

            this.professionalDialog = false;
            this.professional = {};
        }

        async deleteProfessional() {
            try {
                await this.professionalService.deleteProfessional(this.professional._jv.id)
                this.$emit('updated')
                this.toastSuccess.detail = 'Professional Qualifications Deleted'
                this.$toast.add(this.toastSuccess);
            } catch (e) {
                this.toastError.detail = e.message
                this.$toast.add(this.toastError);
                this.professionalService.logError(e).forEach(err => {
                    this.$toast.add({
                        severity: 'error',
                        summary: 'Details',
                        detail: err
                    });
                })
            }

            this.deleteProfessionalDialog = false;
            this.professional = {};
        }

        async deleteSelectedProfessionals() {
            try {
                for (const deleteProfessional of this.selectedProfessionals) {
                    await this.professionalService.deleteProfessional(deleteProfessional._jv.id)
                }
                this.$emit('updated')
                this.toastSuccess.detail = 'Professional Qualifications Deleted'
                this.$toast.add(this.toastSuccess);
            } catch (e) {
                this.toastError.detail = e.message
                this.$toast.add(this.toastError);
                this.professionalService.logError(e).forEach(err => {
                    this.$toast.add({
                        severity: 'error',
                        summary: 'Details',
                        detail: err
                    });
                })
            }
            this.deleteProfessionalsDialog = false;
            this.selectedProfessionals = [];
        }

        async toggleSelectedProfessionals() {
            try {
                if (this.verifyProfessionals) {
                    // verify
                    for (const verifyProfessional of this.selectedProfessionals) {
                        const professionalObj = {
                            'verified': 1,
                            'verified-by': store.state.staffId,
                            'verified-at': this.$moment().toISOString(),
                            _jv: {
                                id: verifyProfessional._jv.id,
                                type: 'staff-professionals',
                                relationships: {
                                    professional: {data: {id: verifyProfessional.professional._jv.id, type: 'professionals'}},
                                    staff: {data: {id: verifyProfessional.staff._jv.id, type: 'staffs'}}
                                }
                            }
                        };
                        await this.professionalService.updateProfessional(professionalObj)
                    }
                }

                if (!this.verifyProfessionals) {
                    // unverify
                    for (const verifyProfessional of this.selectedProfessionals) {
                        const professionalObj = {
                            'verified': 0,
                            'verified-by': null,
                            'verified-at': null,
                            _jv: {
                                id: verifyProfessional._jv.id,
                                type: 'staff-professionals',
                                relationships: {
                                    professional: {data: {id: verifyProfessional.professional._jv.id, type: 'professionals'}},
                                    staff: {data: {id: verifyProfessional.staff._jv.id, type: 'staffs'}}
                                }
                            }
                        };
                        await this.professionalService.updateProfessional(professionalObj)
                    }
                }

                this.$emit('updated')
                this.toastSuccess.detail = 'Professional Qualifications Updated'
                this.$toast.add(this.toastSuccess);
            } catch (e) {
                this.toastError.detail = e.message
                this.$toast.add(this.toastError);
                this.professionalService.logError(e).forEach(err => {
                    this.$toast.add({
                        severity: 'error',
                        summary: 'Details',
                        detail: err,
                    });
                })
            }

            this.toggleProfessionalsDialog = false;
            this.selectedProfessionals = [];
        }
    }


</script>

<style scoped>

</style>
